import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const HeatmapGraph = ({ data, soundmeterId, weighting, colorScheme = 'default' }) => {
  if (!data || !data.data || data.data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Aucune donnée disponible pour la carte de chaleur
        </CardContent>
      </Card>
    );
  }

  const colorSchemes = {
    default: {
      low: '#10b981',
      medium: '#f59e0b',
      high: '#ef4444'
    },
    ocean: {
      low: '#06b6d4',
      medium: '#3b82f6',
      high: '#8b5cf6'
    },
    forest: {
      low: '#84cc16',
      medium: '#22c55e',
      high: '#15803d'
    },
    sunset: {
      low: '#fbbf24',
      medium: '#f97316',
      high: '#dc2626'
    }
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.default;

  // Process data into a grid format for heatmap
  // Group by hour and day of week
  const heatmapData = [];
  const hourlyStats = {};

  data.data.forEach((point, index) => {
    const timestamp = point.timestamp ? new Date(point.timestamp) : new Date(Date.now() - (data.data.length - index) * 3600000);
    const hour = timestamp.getHours();
    const dayOfWeek = timestamp.getDay();
    const value = point.value || point.dB_A || 0;

    const key = `${dayOfWeek}-${hour}`;
    
    if (!hourlyStats[key]) {
      hourlyStats[key] = {
        values: [],
        hour: hour,
        day: dayOfWeek,
        count: 0
      };
    }
    
    hourlyStats[key].values.push(value);
    hourlyStats[key].count++;
  });

  // Calculate average for each hour-day combination
  Object.keys(hourlyStats).forEach(key => {
    const stat = hourlyStats[key];
    const avg = stat.values.reduce((a, b) => a + b, 0) / stat.values.length;
    
    heatmapData.push({
      hour: stat.hour,
      day: stat.day,
      value: avg,
      count: stat.count
    });
  });

  // Find min and max for color scaling
  const values = heatmapData.map(d => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;

  // Color function based on value
  const getColor = (value) => {
    const normalized = (value - minValue) / range;
    
    if (normalized < 0.33) return colors.low;
    if (normalized < 0.67) return colors.medium;
    return colors.high;
  };

  const daysOfWeek = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  // Alternative grid-based heatmap visualization
  const renderGridHeatmap = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const days = Array.from({ length: 7 }, (_, i) => i);

    return (
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex">
            {/* Day labels */}
            <div className="flex flex-col justify-around py-2 pr-2">
              <div className="h-8"></div>
              {days.map(day => (
                <div key={day} className="h-8 flex items-center justify-end text-xs font-medium">
                  {daysOfWeek[day]}
                </div>
              ))}
            </div>
            
            {/* Heatmap grid */}
            <div className="flex-1">
              {/* Hour labels */}
              <div className="flex mb-1">
                {hours.map(hour => (
                  <div key={hour} className="flex-1 text-center text-xs font-medium">
                    {hour}h
                  </div>
                ))}
              </div>
              
              {/* Grid cells */}
              {days.map(day => (
                <div key={day} className="flex mb-1">
                  {hours.map(hour => {
                    const cellData = heatmapData.find(d => d.hour === hour && d.day === day);
                    const value = cellData ? cellData.value : null;
                    const bgColor = value !== null ? getColor(value) : '#f1f5f9';
                    
                    return (
                      <div 
                        key={`${day}-${hour}`}
                        className="flex-1 aspect-square border border-white rounded-sm cursor-pointer transition-transform hover:scale-110"
                        style={{ backgroundColor: bgColor }}
                        title={value !== null ? `${daysOfWeek[day]} ${hour}h: ${value.toFixed(1)} dB (${cellData.count} mesures)` : 'Pas de données'}
                      >
                        {value !== null && (
                          <div className="w-full h-full flex items-center justify-center text-xs font-semibold text-white drop-shadow-lg">
                            {value.toFixed(0)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Calculate peak hours
  const peakHours = heatmapData
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);

  const quietHours = heatmapData
    .sort((a, b) => a.value - b.value)
    .slice(0, 3);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Carte de Chaleur Temporelle - {weighting}</span>
          <div className="flex space-x-2">
            <Badge variant="outline" className="bg-red-50">
              Max: {maxValue.toFixed(1)} dB
            </Badge>
            <Badge variant="outline" className="bg-green-50">
              Min: {minValue.toFixed(1)} dB
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Grid Heatmap */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium mb-4">Niveaux sonores par heure et jour de la semaine</h4>
          {renderGridHeatmap()}
        </div>

        {/* Color Legend */}
        <div className="mb-6 p-4 bg-white rounded-lg border">
          <h4 className="font-medium mb-3">Légende</h4>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.low }}></div>
              <span className="text-sm">Faible ({minValue.toFixed(0)}-{(minValue + range * 0.33).toFixed(0)} dB)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.medium }}></div>
              <span className="text-sm">Moyen ({(minValue + range * 0.33).toFixed(0)}-{(minValue + range * 0.67).toFixed(0)} dB)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.high }}></div>
              <span className="text-sm">Élevé ({(minValue + range * 0.67).toFixed(0)}-{maxValue.toFixed(0)} dB)</span>
            </div>
          </div>
        </div>

        {/* Peak Hours Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <h4 className="font-medium mb-3 text-red-800">Heures les Plus Bruyantes</h4>
            <div className="space-y-2">
              {peakHours.map((hour, index) => (
                <div key={index} className="bg-white p-3 rounded flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{daysOfWeek[hour.day]} à {hour.hour}h</div>
                    <div className="text-xs text-slate-500">{hour.count} mesures</div>
                  </div>
                  <div className="text-2xl font-bold text-red-600">{hour.value.toFixed(1)} dB</div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium mb-3 text-green-800">Heures les Plus Calmes</h4>
            <div className="space-y-2">
              {quietHours.map((hour, index) => (
                <div key={index} className="bg-white p-3 rounded flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{daysOfWeek[hour.day]} à {hour.hour}h</div>
                    <div className="text-xs text-slate-500">{hour.count} mesures</div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">{hour.value.toFixed(1)} dB</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Daily Patterns */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-3">Patterns Hebdomadaires</h4>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, day) => {
              const dayData = heatmapData.filter(d => d.day === day);
              const dayAvg = dayData.length > 0
                ? dayData.reduce((sum, d) => sum + d.value, 0) / dayData.length
                : 0;
              
              return (
                <div key={day} className="bg-white p-3 rounded text-center">
                  <div className="font-medium text-sm">{daysOfWeek[day]}</div>
                  <div className="text-xl font-bold text-teal-600 mt-2">
                    {dayAvg.toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-400">dB moy.</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info Summary */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Sonomètre:</span>
            <div className="font-semibold">{soundmeterId}</div>
          </div>
          <div>
            <span className="text-slate-500">Pondération:</span>
            <div className="font-semibold">{weighting}</div>
          </div>
          <div>
            <span className="text-slate-500">Cellules analysées:</span>
            <div className="font-semibold">{heatmapData.length}</div>
          </div>
          <div>
            <span className="text-slate-500">Période:</span>
            <div className="font-semibold">7 jours × 24h</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HeatmapGraph;
